import { Directive, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { ChargebeeService } from './chargebee.service';

/**
 * Drop-in replacement for the Chargebee Angular wrapper's `cbCardField` directive.
 * Emits a tokenizable component on `(ready)`.
 */
@Directive({
    selector: '[cbCardField]',
    standalone: true,
})
export class CbCardFieldDirective implements OnInit, OnDestroy {
    @Output() ready = new EventEmitter<any>();

    private component: any | null = null;
    private destroyed = false;

    constructor(private host: ElementRef<HTMLElement>, private zone: NgZone, private chargebee: ChargebeeService) {}

    async ngOnInit(): Promise<void> {
        // Avoid running 3rd-party code inside Angular change detection.
        await this.zone.runOutsideAngular(async () => {
            const cb = await this.chargebee.getInstance();
            if (this.destroyed) {
                return;
            }

            const cardComponent = cb.createComponent('card', {
                placeholder: {
                    number: '* Card Number',
                },
            });

            cardComponent.mount(this.host.nativeElement);
            this.component = cardComponent;

            const api = this.createCompatibleApi(cardComponent);

            // Emit after Chargebee reports the component is ready.
            cardComponent.on('ready', () => {
                if (this.destroyed) {
                    return;
                }
                this.zone.run(() => this.ready.emit(api));
            });
        });
    }

    ngOnDestroy(): void {
        this.destroyed = true;

        try {
            this.component?.unmount?.();
        } catch {
            // no-op
        }
        this.component = null;
    }

    private createCompatibleApi(cardComponent: any): any {
        const api: any = {
            tokenize: (additionalData?: any) => cardComponent.tokenize(additionalData),
            focus: (..._args: any[]) => cardComponent.focus(),
        };

        Object.defineProperty(api, 'placeholder', {
            get: () => undefined,
            set: (value: string) => {
                try {
                    cardComponent.update({
                        placeholder: { number: value },
                    });
                } catch {
                    // no-op
                }
            },
            enumerable: true,
        });

        return api;
    }
}

